import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import { Layout } from "../components/Layout";
import api from "../services/api";
import { purchaseOrderService } from "../services/purchaseOrder.service";
import { toLocaleDateString } from "../utils/dateUtils";

interface Client {
  id: number;
  companyName: string;
}

interface EligiblePatient {
  patientID: number;
  firstname: string;
  lastname: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zipcode: string;
  externalLookup: string | null;
  glTestRate: number;
  strips: number;
  lancets: number;
  controlSolution: number;
  lastOrderDate: string | null;
}

const GenerateResupplyPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Calculate default resupply month (next month)
  const getNextMonth = () => {
    const today = new Date();
    const nextMonthIndex = (today.getMonth() + 1) % 12;
    return months[nextMonthIndex];
  };

  const getDefaultYear = () => {
    const today = new Date();
    // If current month is December, next month is January of next year
    return today.getMonth() === 11
      ? today.getFullYear() + 1
      : today.getFullYear();
  };

  // Step 1: Selection
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientID, setSelectedClientID] = useState<number>(0);
  const [resupplyMonth, setResupplyMonth] = useState(getNextMonth());
  const [resupplyYear, setResupplyYear] = useState(getDefaultYear());
  const [includeControlSolution, setIncludeControlSolution] = useState(false);
  const [poNumber, setPoNumber] = useState("");

  // Step 2: Preview
  const [eligiblePatients, setEligiblePatients] = useState<EligiblePatient[]>(
    [],
  );

  const steps = ["Select Client & Date", "Preview Patients", "Generate Order"];

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      const response = await api.get("/clients");
      setClients(response.data.clients || []);
    } catch (err: any) {
      setError("Failed to load clients");
    }
  };

  const calculateReferenceDate = (): Date => {
    // Reference date is 3 months (90 days) before the resupply month
    const monthIndex = months.indexOf(resupplyMonth);
    const resupplyDate = new Date(resupplyYear, monthIndex, 15); // 15th of resupply month
    const referenceDate = new Date(resupplyDate);
    referenceDate.setMonth(referenceDate.getMonth() - 3);
    return referenceDate;
  };

  const handleLoadPatients = async () => {
    if (selectedClientID === 0 || !resupplyMonth) {
      setError("Please select a client and resupply month");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const referenceDate = calculateReferenceDate();

      // Load eligible patients and next PO number in parallel
      const [patientsResponse, nextPo] = await Promise.all([
        api.post("/resupply/eligible-patients", {
          clientID: selectedClientID,
          referenceDate: referenceDate.toISOString(),
          includeControlSolution,
        }),
        purchaseOrderService.getNextPoNumber(),
      ]);

      setEligiblePatients(patientsResponse.data.patients || []);
      setPoNumber(nextPo);
      setActiveStep(1);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load eligible patients");
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePO = async () => {
    if (!poNumber.trim()) {
      setError("Please enter a PO number");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const referenceDate = calculateReferenceDate();
      const selectedClient = clients.find((c) => c.id === selectedClientID);

      if (!selectedClient) {
        throw new Error("Client not found");
      }

      const response = await api.post("/resupply/generate-po", {
        clientID: selectedClientID,
        clientName: selectedClient.companyName,
        poNumber,
        resupplyMonth,
        resupplyYear,
        referenceDate: referenceDate.toISOString(),
        includeControlSolution,
      });

      setSuccess(
        `Resupply order created successfully with ${eligiblePatients.length} patients`,
      );
      setActiveStep(2);

      // Navigate to PO after 2 seconds
      setTimeout(() => {
        navigate(`/purchase-orders/${response.data.purchaseOrderID}`);
      }, 2000);
    } catch (err: any) {
      setError(
        err.response?.data?.error || "Failed to generate resupply order",
      );
    } finally {
      setLoading(false);
    }
  };

  const getTotals = () => {
    return eligiblePatients.reduce(
      (acc, p) => ({
        strips: acc.strips + p.strips,
        lancets: acc.lancets + p.lancets,
        controlSolution: acc.controlSolution + p.controlSolution,
      }),
      { strips: 0, lancets: 0, controlSolution: 0 },
    );
  };

  const totals = getTotals();

  return (
    <Layout>
      <Box>
        <Typography variant='h4' mb={3}>
          Generate Resupply Order
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity='error' onClose={() => setError("")} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity='success' sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {activeStep === 0 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant='h6' mb={3}>
              Step 1: Select Client and Resupply Period
            </Typography>

            <Box display='flex' flexDirection='column' gap={2}>
              <TextField
                select
                label='Client'
                value={selectedClientID}
                onChange={(e) => setSelectedClientID(Number(e.target.value))}
                fullWidth
                required
              >
                <MenuItem value={0}>Select a client...</MenuItem>
                {clients.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    {client.companyName}
                  </MenuItem>
                ))}
              </TextField>

              <Box display='flex' gap={2}>
                <TextField
                  select
                  label='Resupply Month'
                  value={resupplyMonth}
                  onChange={(e) => setResupplyMonth(e.target.value)}
                  fullWidth
                  required
                >
                  <MenuItem value=''>Select month...</MenuItem>
                  {months.map((month) => (
                    <MenuItem key={month} value={month}>
                      {month}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  type='number'
                  label='Resupply Year'
                  value={resupplyYear}
                  onChange={(e) => setResupplyYear(Number(e.target.value))}
                  fullWidth
                  required
                />
              </Box>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeControlSolution}
                    onChange={(e) =>
                      setIncludeControlSolution(e.target.checked)
                    }
                  />
                }
                label='Include Control Solution'
              />

              {resupplyMonth && (
                <Alert severity='info'>
                  Reference Date:{" "}
                  {toLocaleDateString(calculateReferenceDate().toISOString())}
                  <br />
                  (Patients must not have ordered since this date)
                </Alert>
              )}

              <Box display='flex' gap={2} justifyContent='flex-end' mt={2}>
                <Button onClick={() => navigate("/purchase-orders")}>
                  Cancel
                </Button>
                <Button
                  variant='contained'
                  onClick={handleLoadPatients}
                  disabled={loading || selectedClientID === 0 || !resupplyMonth}
                >
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    "Load Eligible Patients"
                  )}
                </Button>
              </Box>
            </Box>
          </Paper>
        )}

        {activeStep === 1 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant='h6' mb={2}>
              Step 2: Preview Eligible Patients ({eligiblePatients.length})
            </Typography>

            <Alert severity='info' sx={{ mb: 2 }}>
              Comment: "{resupplyMonth} {resupplyYear} Resupply Order"
            </Alert>

            <TableContainer sx={{ mb: 3, maxHeight: 500 }}>
              <Table stickyHeader size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Patient ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>External ID</TableCell>
                    <TableCell>GL Rate</TableCell>
                    <TableCell align='right'>Strips</TableCell>
                    <TableCell align='right'>Lancets</TableCell>
                    {includeControlSolution && (
                      <TableCell align='right'>Control Solution</TableCell>
                    )}
                    <TableCell>Last Order</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {eligiblePatients.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align='center'>
                        No eligible patients found
                      </TableCell>
                    </TableRow>
                  ) : (
                    <>
                      {eligiblePatients.map((patient) => (
                        <TableRow key={patient.patientID}>
                          <TableCell>{patient.patientID}</TableCell>
                          <TableCell>
                            {patient.lastname}, {patient.firstname}
                          </TableCell>
                          <TableCell>{patient.externalLookup || "—"}</TableCell>
                          <TableCell>{patient.glTestRate}</TableCell>
                          <TableCell align='right'>{patient.strips}</TableCell>
                          <TableCell align='right'>{patient.lancets}</TableCell>
                          {includeControlSolution && (
                            <TableCell align='right'>
                              {patient.controlSolution}
                            </TableCell>
                          )}
                          <TableCell>
                            {patient.lastOrderDate
                              ? toLocaleDateString(patient.lastOrderDate)
                              : "Never"}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: "#f5f5f5", fontWeight: "bold" }}>
                        <TableCell colSpan={4} align='right'>
                          <strong>Totals:</strong>
                        </TableCell>
                        <TableCell align='right'>
                          <strong>{totals.strips}</strong>
                        </TableCell>
                        <TableCell align='right'>
                          <strong>{totals.lancets}</strong>
                        </TableCell>
                        {includeControlSolution && (
                          <TableCell align='right'>
                            <strong>{totals.controlSolution}</strong>
                          </TableCell>
                        )}
                        <TableCell></TableCell>
                      </TableRow>
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TextField
              label='PO Number'
              value={poNumber}
              onChange={(e) => setPoNumber(e.target.value)}
              fullWidth
              required
              sx={{ mb: 2 }}
            />

            <Box display='flex' gap={2} justifyContent='flex-end'>
              <Button onClick={() => setActiveStep(0)} disabled={loading}>
                Back
              </Button>
              <Button
                variant='contained'
                onClick={handleGeneratePO}
                disabled={loading || eligiblePatients.length === 0 || !poNumber}
              >
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  "Generate Resupply Order"
                )}
              </Button>
            </Box>
          </Paper>
        )}

        {activeStep === 2 && (
          <Paper sx={{ p: 3, textAlign: "center" }}>
            <Typography variant='h6' color='success.main' mb={2}>
              ✓ Resupply Order Generated Successfully
            </Typography>
            <Typography variant='body1' mb={3}>
              Redirecting to purchase order...
            </Typography>
            <CircularProgress />
          </Paper>
        )}
      </Box>
    </Layout>
  );
};

export default GenerateResupplyPage;
